import { useState } from 'react';
import { Link } from 'react-router-dom';
import { trackingApi } from '../api/services';
import toast from 'react-hot-toast';
import { Upload, Loader2, FileText, CheckCircle, XCircle, Pause, AlertTriangle } from 'lucide-react';

function StatSection({ title, rows, icon: Icon, tone }) {
  if (!rows || rows.length === 0) return null;
  const palette = {
    success: 'bg-green-50 border-green-200 text-green-900',
    danger:  'bg-red-50 border-red-200 text-red-900',
    warn:    'bg-amber-50 border-amber-200 text-amber-900',
    info:    'bg-blue-50 border-blue-200 text-blue-900',
  }[tone];
  return (
    <div className={`border rounded-lg p-3 ${palette}`}>
      <div className="flex items-center gap-2 font-medium text-sm mb-2">
        <Icon className="w-4 h-4" /> {title} ({rows.length})
      </div>
      <ul className="text-xs space-y-0.5 font-mono">
        {rows.slice(0, 50).map((r, i) => (
          <li key={i}>
            {r.orderId ? (
              <Link to={`/orders/${r.orderId}`} className="hover:underline">
                {r.orderId.slice(0, 8)}
              </Link>
            ) : (r.orderIdExternal || r.order_id_external || '?')}
            {r.reason && <span className="ml-2 opacity-70">{r.reason}</span>}
          </li>
        ))}
        {rows.length > 50 && <li className="opacity-70">…and {rows.length - 50} more</li>}
      </ul>
    </div>
  );
}

export default function TrackingUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!file) { toast.error('Pick a file first'); return; }
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await trackingApi.upload(form);
      setResult(res.data);
      const summary = [
        `applied ${res.data.appliedNormal?.length || 0}`,
        res.data.appliedCancel?.length ? `cancelled ${res.data.appliedCancel.length}` : null,
        res.data.flaggedPending?.length ? `pending ${res.data.flaggedPending.length}` : null,
        res.data.unmatched?.length ? `unmatched ${res.data.unmatched.length}` : null,
      ].filter(Boolean).join(' · ');
      toast.success(summary);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Upload tracking file</h1>
      <p className="text-sm text-gray-500 mb-5">
        CSV or XLSX from the vendor. <strong>CANCEL</strong> markers auto-cancel the order.
        <strong> PENDING</strong> leaves the order untouched but writes a note.
        Normal tracking numbers move the order to <em>in_transit</em>.
      </p>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5">
        <label className="block mb-4">
          <span className="block text-sm font-medium text-gray-700 mb-1">Tracking file</span>
          <input
            type="file" accept=".csv,.xlsx"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          {file && (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
              <FileText className="w-3.5 h-3.5" /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </div>
          )}
        </label>
        <button
          type="submit" disabled={!file || uploading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </form>

      {result && (
        <div className="mt-5 space-y-3">
          <StatSection title="Tracking applied" rows={result.appliedNormal} icon={CheckCircle} tone="success" />
          <StatSection title="Orders cancelled by vendor" rows={result.appliedCancel} icon={XCircle} tone="danger" />
          <StatSection title="Pending tracking (no change)" rows={result.flaggedPending} icon={Pause} tone="warn" />
          <StatSection title="Unmatched — order not found" rows={result.unmatched} icon={AlertTriangle} tone="info" />
        </div>
      )}
    </div>
  );
}
